import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import { japanPrefectures, regions } from '../../../mocks/japanPrefectures';

interface Genre {
  id: string;
  name: string;
  color: string;
}

export interface FilterState {
  genreIds: string[];
  prefectureId: string | null;
  keyword: string;
}

interface FilterPanelProps {
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  activeTab: 'global' | 'favorite' | 'unlocked';
}

export function FilterPanel({ filters, onChange, activeTab }: FilterPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [genres, setGenres] = useState<Genre[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [keyword, setKeyword] = useState(filters.keyword);

  const activeCount =
    filters.genreIds.length +
    (filters.prefectureId ? 1 : 0) +
    (filters.keyword.trim() ? 1 : 0);

  useEffect(() => {
    supabase
      .from('artist_genres')
      .select('id, name, color')
      .then(({ data }) => {
        if (data) setGenres(data);
      });
  }, []);

  // キーワードのデバウンス
  useEffect(() => {
    const timer = setTimeout(() => {
      onChange({ ...filters, keyword });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const toggleGenre = (id: string) => {
    const next = filters.genreIds.includes(id)
      ? filters.genreIds.filter((g) => g !== id)
      : [...filters.genreIds, id];
    onChange({ ...filters, genreIds: next });
  };

  const setPrefecture = (id: string | null) => {
    onChange({ ...filters, prefectureId: id });
  };

  const clearAll = () => {
    setKeyword('');
    setSelectedRegion('all');
    onChange({ genreIds: [], prefectureId: null, keyword: '' });
  };

  const filteredPrefectures =
    selectedRegion === 'all'
      ? japanPrefectures
      : japanPrefectures.filter((p) => p.region === selectedRegion);

  return (
    <div className="w-full">
      {/* ── 検索バー + フィルターボタン ── */}
      <div className="flex items-center gap-2">
        {/* キーワード検索 */}
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-search-line text-sm text-zinc-500" />
          </span>
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="アーティスト名・キーワードで検索..."
            className="w-full pl-9 pr-4 py-2 rounded-2xl text-sm bg-zinc-800/40 backdrop-blur-md border border-zinc-700/50 text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-brand-primary/40 transition-colors"
          />
          {keyword && (
            <button
              onClick={() => setKeyword('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer text-zinc-500 hover:text-zinc-100 transition-colors"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {/* フィルターボタン */}
        <motion.button
          onClick={() => setIsOpen((v) => !v)}
          whileHover={{ backgroundColor: isOpen || activeCount > 0 ? 'rgba(34,211,238,0.2)' : 'rgba(63,63,70,0.5)' }}
          whileTap={{ scale: 0.98 }}
          className="relative flex items-center gap-1.5 px-3 py-2 rounded-2xl border text-xs font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap transition-all duration-200"
          style={{
            background: isOpen || activeCount > 0 ? 'rgba(34,211,238,0.15)' : 'rgba(39,39,42,0.4)',
            borderColor: isOpen || activeCount > 0 ? 'rgba(34,211,238,0.4)' : 'rgba(63,63,70,0.5)',
            color: isOpen || activeCount > 0 ? '#22d3ee' : 'rgba(244,244,245,0.4)',
          }}
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-equalizer-3-line text-sm" />
          </span>
          <span>FILTER</span>
          {activeCount > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-black bg-brand-primary text-zinc-900"
            >
              {activeCount}
            </span>
          )}
        </motion.button>

        {/* クリアボタン */}
        <AnimatePresence>
          {activeCount > 0 && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ backgroundColor: 'rgba(63,63,70,0.5)' }}
              whileTap={{ scale: 0.98 }}
              onClick={clearAll}
              className="flex items-center gap-1 px-2.5 py-2 rounded-2xl border text-xs font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap transition-all duration-200 bg-zinc-800/40 border-zinc-700/50 text-zinc-500"
            >
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-close-line text-xs" />
              </span>
              CLEAR
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* ── フィルターパネル ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div
              className="mt-3 rounded-2xl p-4 space-y-5 bg-zinc-800/40 backdrop-blur-md border border-zinc-700/50"
              style={{
                boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
              }}
            >
              {/* ── ジャンル ── */}
              <div>
                <p className="text-[10px] font-black tracking-[0.3em] uppercase text-zinc-500 mb-2.5">
                  <span className="w-3 h-3 inline-flex items-center justify-center mr-1">
                    <i className="ri-music-2-line" />
                  </span>
                  GENRE
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {genres.map((g) => {
                    const active = filters.genreIds.includes(g.id);
                    return (
                      <motion.button
                        key={g.id}
                        onClick={() => toggleGenre(g.id)}
                        whileHover={{ backgroundColor: active ? undefined : 'rgba(63,63,70,0.5)' }}
                        whileTap={{ scale: 0.98 }}
                        className="px-3 py-1 rounded-full text-[11px] font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap transition-all duration-150"
                        style={{
                          background: active ? `${g.color}22` : 'rgba(39,39,42,0.4)',
                          border: `1px solid ${active ? g.color + '66' : 'rgba(63,63,70,0.5)'}`,
                          color: active ? g.color : 'rgba(244,244,245,0.35)',
                        }}
                      >
                        {g.name}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* ── 地域 ── */}
              <div>
                <p className="text-[10px] font-black tracking-[0.3em] uppercase text-zinc-500 mb-2.5">
                  <span className="w-3 h-3 inline-flex items-center justify-center mr-1">
                    <i className="ri-map-pin-2-line" />
                  </span>
                  REGION / PREFECTURE
                </p>

                {/* 地方タブ */}
                <div className="flex flex-wrap gap-1 mb-3">
                  {regions.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => {
                        setSelectedRegion(r.id);
                        if (
                          filters.prefectureId &&
                          r.id !== 'all' &&
                          !japanPrefectures.find(
                            (p) => p.id === filters.prefectureId && p.region === r.id,
                          )
                        ) {
                          setPrefecture(null);
                        }
                      }}
                      className="px-2.5 py-1 rounded-2xl text-[10px] font-bold tracking-wider uppercase cursor-pointer whitespace-nowrap transition-all duration-150"
                      style={{
                        background:
                          selectedRegion === r.id
                            ? 'rgba(34,211,238,0.18)'
                            : 'rgba(39,39,42,0.4)',
                        border: `1px solid ${selectedRegion === r.id ? 'rgba(34,211,238,0.4)' : 'rgba(63,63,70,0.5)'}`,
                        color:
                          selectedRegion === r.id
                            ? '#22d3ee'
                            : 'rgba(244,244,245,0.3)',
                      }}
                    >
                      {r.nameEn}
                    </button>
                  ))}
                </div>

                {/* 都道府県グリッド */}
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1 custom-scrollbar">
                  {filteredPrefectures.map((p) => {
                    const active = filters.prefectureId === p.id;
                    return (
                      <motion.button
                        key={p.id}
                        onClick={() => setPrefecture(active ? null : p.id)}
                        whileHover={{ backgroundColor: active ? undefined : 'rgba(63,63,70,0.5)' }}
                        whileTap={{ scale: 0.98 }}
                        className="px-2.5 py-1 rounded-2xl text-[11px] font-bold tracking-wider cursor-pointer whitespace-nowrap transition-all duration-150"
                        style={{
                          background: active ? `${p.color}22` : 'rgba(39,39,42,0.4)',
                          border: `1px solid ${active ? p.color + '66' : 'rgba(63,63,70,0.5)'}`,
                          color: active ? p.color : 'rgba(244,244,245,0.35)',
                        }}
                      >
                        {p.name}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* ── アクティブフィルター表示 ── */}
              <AnimatePresence>
                {activeCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-wrap items-center gap-1.5 pt-3 border-t border-zinc-700/50"
                  >
                    <span className="text-[10px] tracking-widest text-zinc-500 uppercase mr-1">
                      ACTIVE:
                    </span>
                    {filters.genreIds.map((gid) => {
                      const g = genres.find((x) => x.id === gid);
                      if (!g) return null;
                      return (
                        <span
                          key={gid}
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                          style={{
                            background: `${g.color}22`,
                            border: `1px solid ${g.color}55`,
                            color: g.color,
                          }}
                        >
                          <i className="ri-music-2-line text-[10px]" />
                          {g.name}
                          <button
                            onClick={() => toggleGenre(gid)}
                            className="cursor-pointer hover:opacity-70 transition-opacity ml-0.5"
                          >
                            <i className="ri-close-line text-[10px]" />
                          </button>
                        </span>
                      );
                    })}
                    {filters.prefectureId && (
                      <span
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                        style={{
                          background: 'rgba(34,211,238,0.15)',
                          border: '1px solid rgba(34,211,238,0.4)',
                          color: '#22d3ee',
                        }}
                      >
                        <i className="ri-map-pin-2-line text-[10px]" />
                        {japanPrefectures.find((p) => p.id === filters.prefectureId)?.name}
                        <button
                          onClick={() => setPrefecture(null)}
                          className="cursor-pointer hover:opacity-70 transition-opacity ml-0.5"
                        >
                          <i className="ri-close-line text-[10px]" />
                        </button>
                      </span>
                    )}
                    {filters.keyword.trim() && (
                      <span
                        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-700/50 border border-zinc-600 text-zinc-300"
                      >
                        <i className="ri-search-line text-[10px]" />
                        &quot;{filters.keyword}&quot;
                        <button
                          onClick={() => { setKeyword(''); onChange({ ...filters, keyword: '' }); }}
                          className="cursor-pointer hover:opacity-70 transition-opacity ml-0.5"
                        >
                          <i className="ri-close-line text-[10px]" />
                        </button>
                      </span>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
